import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useExportHistoryHook } from "@/hooks/useExportHistory";
import PaymentHistoryTable from "@/components/payment-history/PaymentHistoryTable";
import { exportHistoryTableColumns } from "@/components/export-history/ExportHistoryTableColumns";
import { Button } from "@/common/ui/Button";
import Icon from "@/common/ui/Image";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { loginSelector } from "@/store/features/loginSlice";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { isHasPermission } from "@/utils/helper/permissions";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";

const ExportHistoryPage: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageEvent = useSelector(pageEventSelector);
  const { currentUser } = useSelector(loginSelector);
  
  // Permission check for export history
  const canExportPayments = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Export Payments"]);
  
  // Check if user has permission to view export history
  if (!canExportPayments) {
    return <PermissionDenied />;
  }
  
  const {
    exportHistoryData,
    isLoading,
    error,
    refreshData,
    totalCount,
    setPagination,
    pagination,
    setSortBy,
  } = useExportHistoryHook(!canExportPayments);

  // Use Redux data from API, fallback to empty array if no data
  const displayData = exportHistoryData && exportHistoryData.length > 0 ? exportHistoryData : [];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      // Add a small delay to ensure the loading state is visible
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };


  // Pagination handlers
  const handlePageChange = (page: number) => {
    setPagination({
      pageNumber: page,
      pageSize: pagination.pageSize, // Keep current page size
      page: page,
      per_page: pagination.pageSize,
      total: totalCount || 0,
      total_pages: Math.ceil((totalCount || 0) / pagination.pageSize),
    });
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination({
      pageNumber: 1, // Reset to first page when changing page size
      pageSize: perPage,
      page: 1,
      per_page: perPage,
      total: totalCount || 0,
      total_pages: Math.ceil((totalCount || 0) / perPage),
    });
  };

  // Sort handler
  const handleSortChange = (newSortBy: Array<{ field: string; isASC: boolean }>) => {
    setSortBy(newSortBy);
    // Reset to first page when sorting changes
    setPagination({
      pageNumber: 1,
      pageSize: pagination.pageSize,
      page: 1,
      per_page: pagination.pageSize,
      total: totalCount || 0,
      total_pages: Math.ceil((totalCount || 0) / pagination.pageSize),
    });
    // Data will be refreshed automatically by the useExportHistoryHook
  };

  useEffect(() => {
    if (pageEvent?.keys.includes("exportHistory") && pageEvent?.type === "refresh") {
      refreshData();
    }
  }, [pageEvent, refreshData]);

  return (
    <div>
      <div >
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-1 px-3 sm:px-4 lg:px-[10px]">
          <div className="flex items-center gap-3">
            <div className="flex items-center">
              <p className="text-sm sm:text-[16px] font-[700] text-gray-700 mr-2">Export History ({totalCount || 0})</p>
              <Button 
                variant="transparent" 
                size="small" 
                className="!p-1 !min-w-0" 
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <Icon 
                  src="/icons/refresh.svg" 
                  alt="refresh icon" 
                  className={`w-6 h-6 ${isRefreshing ? 'animate-spin' : ''}`} 
                />
              </Button>
            </div>
          </div>
        </div>

        {/* Main table content */}
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm h-[calc(100vh-160px)] flex flex-col">
            <PaymentHistoryTable
            enableRowSelection={false}
            enableMoreBtn={false}
              data={displayData.map((item: any) => ({ 
                ...item, 
                id: item.export_id || item.id?.toString() || Math.random().toString() 
              }))}
              columnsConfig={exportHistoryTableColumns as any}
              dropdownOptions={[]}
              rowSelection={{}}
              setRowSelection={() => {}}
              openModal={() => {}}
              paginationConfig={{
                initialPage: 1,
                initialPerPage: 25,
                perPageOptions: [10, 25, 50, 100],
              }}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              totalCount={totalCount}
              currentPage={pagination.pageNumber}
              currentPerPage={pagination.pageSize}
              sortBy={[]}
              onSortChange={handleSortChange}
              className="flex-1 min-w-full"
              emptyStateText="No export history found!"
              emptyStateIcon="/icons/empty-list-file.svg"
              isLoading={isLoading || isRefreshing}
              error={error}
              onRetry={refreshData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportHistoryPage;